import { StickyNote, Wrench, Copy, Download, Loader2 } from "lucide-react";
import { EmptyTabState } from "./EmptyTabState";
import { LineSheetsHeaderSection } from "./LineSheets";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import type { AddNoteBody, EquipmentCount } from "@/interfaces/estimation.interface";
import { toast } from "sonner";
import type { BarShapeProps } from "recharts";
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import type { ActiveDotProps } from "recharts/types/util/types";
import { useUser } from "@/hooks/useUser";
import type { SelectedLineItem } from "@/redux/slices/estimation.slice";
import type { EstimateDetail, LineItem, ScopeNoteMapping } from "@/interfaces/estimate-detail";
import estimateProvider from "@/providers/estimate.provider";
import { Button } from "@/components/ui/button";
import { PersonBadge } from "@/components/dashboard/PersonBadge";
import { DateBadge } from "@/components/dashboard/DateBadge";

const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;
const HIGHLIGHT_COLOR = "#fef08a";

type HighlightedSegment = {
  text: string;
  isHighlighted: boolean;
  startIndex: number;
  endIndex: number;
};

interface ScopeNotesProps {
  estimateId: string;
  showHighlightedWord?: boolean;
  showHeader?: boolean;
}

const normalizeString = (str: string): string => str.toLowerCase().replace(/\s/g, "").trim();

export const ScopeNotes = ({ estimateId, showHighlightedWord, showHeader = false }: ScopeNotesProps) => {
  const { estimation } = useEstimateDetail(estimateId);
  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);

  const scopeNoteObjects = useMemo(
    () =>
      estimation?.scopeNoteObjects
        ? [...estimation.scopeNoteObjects].sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
        : [],

    [estimation],
  );

  const lineItemEquipmentCounts = useMemo(
    () =>
      (estimation?.mitigationDetails?.equipmentCount || []).filter(
        (count) =>
          normalizeString(count.lineItemString) === normalizeString(lineItemDetail?.lineItem.lineItem || "") &&
          normalizeString(count.roomName) === normalizeString(lineItemDetail?.tuple.pageMetadata?.roomName || ""),
      ),
    [estimation?.mitigationDetails?.equipmentCount, lineItemDetail?.lineItem.lineItem, lineItemDetail?.tuple.pageMetadata?.roomName],
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadScopeNotes = async () => {
    setIsDownloading(true);
    try {
      const response = await estimateProvider.downloadScopeNotes(estimateId);
      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with project name and timestamp (matching backend format)
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const timestamp = `${month}-${day}-${year}_${hours}-${minutes}`;

      // Sanitize project name for filename (remove invalid characters)
      const sanitizedProjectName = (estimation?.projectName || "scope-notes")
        .replace(/[<>:"/\\|?*]/g, "-") // Replace invalid filename characters
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens

      link.download = `${sanitizedProjectName}-${timestamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Failed to download scope notes");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!lineItemDetail?.tuple.hasError && lineItemEquipmentCounts.length > 1) {
    return <EquipmentCountOverview estimateId={estimateId} equipmentCounts={lineItemEquipmentCounts} scopeNoteObjects={scopeNoteObjects} />;
  } else {
    const isSingleNote = scopeNoteObjects.length === 1;
    const notesContent = (
      <div className={`flex flex-col gap-1 ${isSingleNote ? "min-h-0 flex-1" : ""}`}>
        {scopeNoteObjects.map((scopeNoteObject) => (
          <NoteWithToggle
            key={`note_wrapper_${scopeNoteObject.uuid}`}
            estimateId={estimateId}
            scopeNoteObject={scopeNoteObject}
            showHighlightedWord={showHighlightedWord}
            fillVerticalSpace={isSingleNote}
          />
        ))}
      </div>
    );

    if (showHeader) {
      if (scopeNoteObjects.length === 0) {
        return (
          <EmptyTabState
            icon={<StickyNote className="text-muted-foreground h-6 w-6" />}
            title="No notes"
            description="No scope notes found for this estimate."
          />
        );
      }
      return (
        <div className="flex h-[95%] w-full flex-col gap-2">
          <div className="flex justify-end px-4 py-2">
            {isDownloading ? (
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            ) : (
              <Download
                onClick={handleDownloadScopeNotes}
                className="text-muted-foreground hover:text-foreground h-5 w-5 cursor-pointer transition-colors"
              />
            )}
          </div>
          {notesContent}
        </div>
      );
    }

    return notesContent;
  }
};

const EquipmentCountOverview = ({
  estimateId,
  equipmentCounts,
  scopeNoteObjects,
}: {
  estimateId: string;
  equipmentCounts: EquipmentCount[];
  scopeNoteObjects: AddNoteBody[];
}) => {
  const equipmentCountsWithTimestamps: (EquipmentCount & { timestamp: number })[] = useMemo(
    () =>
      equipmentCounts
        .map((equipmentCount) => {
          const scopeNoteObject = scopeNoteObjects.find((scopeNoteObject) => scopeNoteObject.uuid === equipmentCount.scopeNoteObjectUuid)!;
          const timestamp = new Date(scopeNoteObject.dateCreated).getTime();
          return {
            ...equipmentCount,
            timestamp,
          };
        })
        .sort((a, b) => b.timestamp - a.timestamp),
    [equipmentCounts],
  );

  const chartData = useMemo(() => {
    const equipmentCountsWithTimestampsAsc = [...equipmentCountsWithTimestamps].reverse();

    const firstEquipmentCount = equipmentCountsWithTimestampsAsc[0];

    const results: { quantity: number; timestamp: number; isOriginalData: boolean }[] = [
      {
        timestamp: firstEquipmentCount.timestamp,
        quantity: firstEquipmentCount.quantity,
        isOriginalData: true,
      },
    ];

    for (const equipmentCount of equipmentCountsWithTimestampsAsc.slice(1)) {
      const prevResults = results[results.length - 1]!;

      const numDaysBetween = Math.floor((equipmentCount.timestamp - prevResults.timestamp) / MILLISECONDS_IN_DAY) - 1;

      const inBetweenDays = Array.from({ length: numDaysBetween }).map((_, i) => ({
        timestamp: prevResults.timestamp + MILLISECONDS_IN_DAY * (i + 1),
        quantity: prevResults.quantity,
        isOriginalData: false,
      }));

      results.push(...inBetweenDays);
      results.push({
        timestamp: equipmentCount.timestamp,
        quantity: equipmentCount.quantity,
        isOriginalData: true,
      });
    }

    return results.map(({ timestamp, quantity, isOriginalData }) => {
      const dateTime = new Date(timestamp);
      dateTime.setHours(0, 0, 0, 0);

      return {
        date: dateTime,
        barDate: dateTime.getTime() + MILLISECONDS_IN_DAY * 0.5,
        dateLabel: dateTime.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        quantity: quantity,
        fullDate: dateTime.toLocaleDateString(),
        showDot: isOriginalData,
      };
    });
  }, [equipmentCountsWithTimestamps]);

  return (
    <div className="flex flex-col gap-2">
      <LineSheetsHeaderSection title={`Equipment Use Over Time`}>
        <Wrench className="h-4 w-4" />
      </LineSheetsHeaderSection>
      <div className="mx-4 mb-4 rounded-lg border border-[#EEEFF1] bg-white p-4 shadow-sm">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEEFF1" />
              <XAxis
                dataKey="date"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                }
                stroke="#71717A"
                fontSize={12}
              />
              <YAxis stroke="#71717A" fontSize={12} allowDecimals={false} tickCount={6} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #EEEFF1",
                  borderRadius: "6px",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value) => [`${value} units`, `Quantity`]}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0]) {
                    return `${payload[0].payload.dateLabel}`;
                  }
                  return new Date(label).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  });
                }}
              />

              <Bar
                dataKey="quantity"
                fill="#d1d5db"
                opacity={0.8}
                shape={(props: BarShapeProps) => {
                  const { index, x, y, width, height } = props;
                  const shiftedX = (x as number) + (width as number) * 0.5;

                  // hide the last bar in histogram, shift every bar to the right by half so that the first
                  // bar is vertically aligned with the y-axis
                  if (index !== undefined && index < chartData.length - 1) {
                    return <rect x={shiftedX} y={y} width={width} height={height} fill="#d1d5db" opacity={0.8} />;
                  }
                  return null;
                }}
              />

              <Line
                type="stepAfter"
                dataKey="quantity"
                stroke="#09090B"
                strokeWidth={2}
                dot={(props) => {
                  const { payload, cx, cy } = props as unknown as ActiveDotProps;

                  // only show dots that correspond with scope note objects
                  if (payload?.showDot) {
                    return (<circle cx={cx} cy={cy} r={4} fill="#09090B" strokeWidth={2} stroke="#09090B" />) as any;
                  }
                  return null;
                }}
                activeDot={{ r: 6, fill: "#09090B" }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        {equipmentCountsWithTimestamps.map((equipmentCount) => (
          <EquipmentCountOverviewItem
            equipmentCount={equipmentCount}
            estimateId={estimateId}
            scopeNoteObject={
              scopeNoteObjects.find(
                (scopeNoteObject) => new Date(scopeNoteObject.dateCreated).getTime() === new Date(equipmentCount.timestamp).getTime(),
              )!
            }
          />
        ))}
      </div>
    </div>
  );
};

const EquipmentCountOverviewItem = ({
  equipmentCount,
  estimateId,
  scopeNoteObject,
}: {
  equipmentCount: EquipmentCount;
  estimateId: string;
  scopeNoteObject: AddNoteBody;
}) => {
  const [openDetail, setOpenDetail] = useState<boolean>(false);

  const toggleOpenDetail = useCallback(() => setOpenDetail((_openDetail) => !_openDetail), []);

  return (
    <div
      key={scopeNoteObject.dateCreated}
      className="mx-4 overflow-hidden rounded-lg border border-[#EEEFF1] bg-white shadow-sm transition-colors duration-200 hover:bg-gray-50"
    >
      <div
        className="flex cursor-pointer items-center justify-between px-4 py-3 transition-colors duration-200 hover:bg-gray-50"
        onClick={() => toggleOpenDetail()}
      >
        <div className="flex items-center justify-between">
          <div className="w-20">
            <span className="text-sm text-[#71717A]">
              <ScopeNoteDate scopeNoteObject={scopeNoteObject} estimateId={estimateId} />
            </span>
          </div>

          <div className="w-20">
            <span className="text-sm font-medium text-[#09090B]">{`Qty: ${equipmentCount.quantity}`}</span>
          </div>
        </div>

        <button
          className="ml-6 rounded-sm p-2 transition-colors duration-200 hover:bg-gray-100"
          onClick={(e) => {
            e.stopPropagation();
            toggleOpenDetail();
          }}
        >
          <svg
            className={`h-4 w-4 transform text-[#71717A] transition-transform duration-200 ${openDetail ? "rotate-180" : "rotate-0"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {openDetail && (
        <div className="border-t border-[#EEEFF1]">
          <div className="px-4 py-2">
            <ScopeNote estimateId={estimateId} scopeNoteObject={scopeNoteObject} showHighlightedWord={true} />
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get line item from selectedLineItem (handles merged pages like selectLineItem does)
const getLineItemFromSelection = (selectedLineItem: SelectedLineItem, estimationDetail: EstimateDetail[] | undefined): LineItem | null => {
  if (!estimationDetail) return null;

  // Look for tuple in the current page
  const currentPage = estimationDetail.find((detail) => detail.dataForUI.metadata.pageId === selectedLineItem.pageId);

  // Check if this tuple is in the current page
  let tuple = currentPage?.dataForUI.page.tuplesOutput?.find(
    (current) =>
      current.tupleId === selectedLineItem.tupleId &&
      current.lineItems.some((lineItem) => lineItem.lineItemId === selectedLineItem.lineItemId),
  );

  // If not, check if this tuple is in any page that's merged with the current page
  if (!tuple && currentPage) {
    const mergedPages = estimationDetail.filter((detail) => {
      return detail.dataForUI.page.mergeWith === currentPage?.dataForUI.metadata.pageId && detail.dataForUI.page.tuplesOutput;
    });

    if (mergedPages.length > 0) {
      tuple = mergedPages[0].dataForUI.page.tuplesOutput!.find(
        (current) =>
          current.tupleId === selectedLineItem.tupleId &&
          current.lineItems.some((lineItem) => lineItem.lineItemId === selectedLineItem.lineItemId),
      );
    }
  }

  if (!tuple) return null;

  const lineItem = tuple.lineItems.find((li) => li.lineItemId === selectedLineItem.lineItemId);
  return lineItem || null;
};

// Helper function to create a segment
const createSegment = (text: string, startIndex: number, endIndex: number, isHighlighted: boolean = false): HighlightedSegment => ({
  text,
  isHighlighted,
  startIndex,
  endIndex,
});

// Helper function to filter mappings by scope note UUID and exclude overwritten ones
const filterMappingsByScopeNote = (mappings: ScopeNoteMapping[], scopeNoteUuid: string): ScopeNoteMapping[] => {
  return mappings.filter((mapping) => mapping.scopeNoteUuid === scopeNoteUuid && !mapping.isOverwritten);
};

// Helper function to collect all mappings from selectedLineItems or lineItemDetail
const collectAllMappings = (
  lineItemDetail: { lineItem: LineItem } | undefined,
  selectedLineItems: SelectedLineItem[],
  estimationDetail: EstimateDetail[] | undefined,
  scopeNoteUuid: string,
): ScopeNoteMapping[] => {
  const allMappings: ScopeNoteMapping[] = [];

  // Use selectedLineItems as the source of truth when available
  if (estimationDetail && selectedLineItems.length > 0) {
    for (const selectedItem of selectedLineItems) {
      const lineItem = getLineItemFromSelection(selectedItem, estimationDetail);
      if (lineItem?.scopeNotesMappingsList) {
        const filtered = filterMappingsByScopeNote(lineItem.scopeNotesMappingsList, scopeNoteUuid);
        allMappings.push(...filtered);
      }
    }
  } else if (lineItemDetail?.lineItem.scopeNotesMappingsList) {
    // Fallback to lineItemDetail when no multi-selection is active
    const filtered = filterMappingsByScopeNote(lineItemDetail.lineItem.scopeNotesMappingsList, scopeNoteUuid);
    allMappings.push(...filtered);
  }

  // Sort by startIndex to handle overlapping ranges
  return allMappings.sort((a, b) => a.startIndex - b.startIndex);
};

// Helper function to find the span element with data attributes by walking up the DOM tree
const findSpanWithDataAttribute = (node: Node | null, container: HTMLElement | null): HTMLElement | null => {
  let current: Node | null = node;

  while (current && current !== container) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const element = current as HTMLElement;
      if (element.dataset.startIndex !== undefined) {
        return element;
      }
    }
    current = current.parentNode;
  }

  return null;
};

// Helper function to calculate text position from a Range and span element
const calculateTextPosition = (spanElement: HTMLElement, rangeOffset: number): number => {
  const spanStartIndex = parseInt(spanElement.dataset.startIndex || "0", 10);
  return spanStartIndex + rangeOffset;
};

// Helper function to find line items for a scope note range
const findLineItemsForScopeNoteRange = (
  estimationDetail: EstimateDetail[] | undefined,
  scopeNoteUuid: string,
  startIndex: number,
  endIndex: number,
): string[] => {
  const matches: string[] = [];

  if (!estimationDetail) return matches;

  for (const detail of estimationDetail) {
    const tuples = detail.dataForUI.page.tuplesOutput || [];

    for (const tuple of tuples) {
      for (const lineItem of tuple.lineItems || []) {
        const mappings = lineItem.scopeNotesMappingsList || [];

        for (const mapping of mappings) {
          if (
            mapping.scopeNoteUuid === scopeNoteUuid &&
            !mapping.isOverwritten &&
            // Check if ranges overlap
            mapping.startIndex < endIndex &&
            mapping.endIndex > startIndex
          ) {
            matches.push(lineItem.uniqueId);
            break; // Only add each line item once
          }
        }
      }
    }
  }

  return matches;
};

const NoteWithToggle = ({
  estimateId,
  scopeNoteObject,
  showHighlightedWord,
  fillVerticalSpace,
}: {
  estimateId: string;
  scopeNoteObject: AddNoteBody;
  showHighlightedWord?: boolean;
  fillVerticalSpace?: boolean;
}) => {
  const [showUntranslated, setShowUntranslated] = useState(false);
  const wasTranslated = !!scopeNoteObject.untranslatedText;
  const defaultDisplay = scopeNoteObject.originalText ?? scopeNoteObject.text;
  const displayedText = showUntranslated && scopeNoteObject.untranslatedText ? scopeNoteObject.untranslatedText : defaultDisplay;

  return (
    <div className={`flex flex-col ${fillVerticalSpace ? "min-h-0 flex-1" : ""}`}>
      <div className="flex shrink-0 items-center justify-between pr-4">
        <div className="flex min-w-0 flex-1 items-center gap-1 px-4 text-muted-foreground">
          <StickyNote className="h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1 overflow-hidden text-sm whitespace-nowrap">
            <ScopeNoteDate estimateId={estimateId} scopeNoteObject={scopeNoteObject} showTime />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {wasTranslated && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setShowUntranslated((v) => !v)}
            >
              {showUntranslated ? "Show translated" : "Show original"}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (displayedText) {
                navigator.clipboard.writeText(displayedText);
                toast.success("Copied to clipboard");
              }
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <ScopeNote
        key={`note_${scopeNoteObject.uuid}`}
        estimateId={estimateId}
        scopeNoteObject={scopeNoteObject}
        showHighlightedWord={showHighlightedWord}
        showUntranslated={showUntranslated}
        fillVerticalSpace={fillVerticalSpace}
      />
    </div>
  );
};

const ScopeNote = ({
  estimateId,
  scopeNoteObject,
  showHighlightedWord,
  disableEditing,
  fillVerticalSpace,
  showUntranslated,
}: {
  estimateId: string;
  scopeNoteObject: AddNoteBody;
  showHighlightedWord?: boolean;
  disableEditing?: boolean;
  fillVerticalSpace?: boolean;
  showUntranslated?: boolean;
}) => {
  const { updateOrCreateScopeNoteObject: _updateOrCreateScopeNoteObject, selectLineItem, estimationDetail } = useEstimateDetail(estimateId);

  const { text: scopeNotesText, originalText: originalScopeNotesText, untranslatedText: untranslatedScopeNotesText } = scopeNoteObject;

  const allowScopeNoteEditing = !disableEditing && import.meta.env.VITE_ALLOW_EDIT_SCOPE_NOTES === "true";

  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);
  const selectedLineItems = useSelector(estimationSelector.selectedLineItems);

  const highlightedSegments = useMemo<HighlightedSegment[]>(() => {
    if (scopeNotesText === undefined) {
      return [];
    }

    const defaultText = originalScopeNotesText ?? scopeNotesText;
    const textToUse = showUntranslated && untranslatedScopeNotesText ? untranslatedScopeNotesText : defaultText;
    const isDisplayingProcessableText = textToUse === scopeNotesText;

    // Mapping indices target the canonical `text` field. Skip highlights when displaying
    // any other variant (Xcelerate operator note or untranslated source).
    if (!showHighlightedWord || !isDisplayingProcessableText) {
      return [createSegment(textToUse, 0, textToUse.length)];
    }

    // Collect all mappings from selectedLineItems (or lineItemDetail as fallback)
    const allMappings = collectAllMappings(lineItemDetail, selectedLineItems, estimationDetail, scopeNoteObject.uuid);

    if (allMappings.length === 0) {
      return [createSegment(textToUse, 0, textToUse.length)];
    }

    // Get all unique split points and sort them
    const splitPoints = Array.from(new Set(allMappings.flatMap(({ startIndex, endIndex }) => [startIndex, endIndex]))).sort(
      (a, b) => a - b,
    );

    // Build segments with highlight information
    const segments: HighlightedSegment[] = [];

    // Add segment before first split point if needed
    if (splitPoints[0] > 0) {
      const segmentText = textToUse.slice(0, splitPoints[0]);
      segments.push(createSegment(segmentText, 0, splitPoints[0]));
    }

    // Build segments between split points
    for (let i = 0; i < splitPoints.length - 1; i++) {
      const segmentStart = splitPoints[i];
      const segmentEnd = splitPoints[i + 1];

      const isHighlighted = allMappings.some((mapping) => segmentStart < mapping.endIndex && segmentEnd > mapping.startIndex);

      if (segmentStart < segmentEnd) {
        const segmentText = textToUse.slice(segmentStart, segmentEnd);
        segments.push(createSegment(segmentText, segmentStart, segmentEnd, isHighlighted));
      }
    }

    // Add remaining text after last split point
    if (splitPoints.length > 0) {
      const lastSplitPoint = splitPoints[splitPoints.length - 1];
      if (lastSplitPoint < textToUse.length) {
        const segmentText = textToUse.slice(lastSplitPoint);
        segments.push(createSegment(segmentText, lastSplitPoint, textToUse.length));
      }
    } else {
      segments.push(createSegment(textToUse, 0, textToUse.length));
    }

    return segments;
  }, [
    lineItemDetail,
    selectedLineItems,
    estimationDetail,
    originalScopeNotesText,
    untranslatedScopeNotesText,
    scopeNoteObject.uuid,
    scopeNotesText,
    showHighlightedWord,
    showUntranslated,
  ]);

  const updateOrCreateScopeNoteObject = useCallback(
    async (updatedScopeNotes: string) => {
      // Reconstruct text from segments
      const currentText = highlightedSegments.map((s) => s.text).join("");
      if (updatedScopeNotes === currentText) {
        return;
      }
      await _updateOrCreateScopeNoteObject({
        ...scopeNoteObject,
        text: updatedScopeNotes,
      });
    },
    [highlightedSegments, scopeNoteObject, _updateOrCreateScopeNoteObject],
  );

  const containerRef = useRef<HTMLDivElement>(null);

  // Use Selection API to detect when user selects text in the notes area
  useEffect(() => {
    const handleSelectionEnd = () => {
      const activeSelection = document.getSelection();
      const text = activeSelection?.toString()?.trim();

      // Early returns for invalid selections
      if (!activeSelection || !text || text.length === 0 || activeSelection.rangeCount === 0) {
        return;
      }

      const range = activeSelection.getRangeAt(0);

      // Check if the selection is within our container
      if (!containerRef.current?.contains(range.commonAncestorContainer)) {
        return;
      }

      // Use the currently displayed text for selection-based line item lookup
      const defaultText = originalScopeNotesText ?? scopeNotesText;
      const textToUse = showUntranslated && untranslatedScopeNotesText ? untranslatedScopeNotesText : defaultText;
      if (!textToUse) {
        return;
      }

      // Find span elements with data attributes
      const startSpanElement = findSpanWithDataAttribute(range.startContainer, containerRef.current);
      const endSpanElement = findSpanWithDataAttribute(range.endContainer, containerRef.current);

      // Calculate text position indices
      let startIndex: number;
      let endIndex: number;

      if (!startSpanElement || !endSpanElement) {
        // Fallback: use indexOf if we couldn't find span elements (shouldn't happen normally)
        const selectedTextNormalized = text.trim();
        const fuzzyIndex = textToUse.indexOf(selectedTextNormalized);
        if (fuzzyIndex === -1) {
          return;
        }
        startIndex = fuzzyIndex;
        endIndex = fuzzyIndex + selectedTextNormalized.length;
      } else {
        // Use data attributes to calculate exact positions
        // Works for both single-segment and multi-segment selections
        startIndex = calculateTextPosition(startSpanElement, range.startOffset);
        endIndex = calculateTextPosition(endSpanElement, range.endOffset);
      }

      // Find matching line items and select them
      const matches = findLineItemsForScopeNoteRange(estimationDetail, scopeNoteObject.uuid, startIndex, endIndex);

      if (matches.length === 0) {
        return;
      }

      selectLineItem(matches[0]);
    };

    // Listen for selection changes using mouseup (when selection is complete)
    document.addEventListener("mouseup", handleSelectionEnd);

    return () => {
      document.removeEventListener("mouseup", handleSelectionEnd);
    };
  }, [scopeNotesText, originalScopeNotesText, untranslatedScopeNotesText, scopeNoteObject.uuid, estimationDetail, selectLineItem, showUntranslated]);

  useEffect(() => {
    // Trigger scroll to first highlight when highlightWord changes
    if ((lineItemDetail || selectedLineItems.length > 0) && highlightedSegments.length > 0) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        const highlightedElement = containerRef.current?.querySelector(".highlight-target");
        if (highlightedElement) {
          highlightedElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [lineItemDetail, selectedLineItems.length, highlightedSegments.length]);

  if (highlightedSegments.length === 0) return null;

  return (
    <div className={`flex flex-col gap-2 py-2 ${fillVerticalSpace ? "min-h-0 flex-1" : ""}`}>
      <div className={`relative mx-4 flex flex-col ${fillVerticalSpace ? "min-h-0 flex-1" : ""}`}>
        <div className={`flex flex-col rounded-md border bg-gray-50 ${fillVerticalSpace ? "min-h-0 flex-1" : ""}`}>
          <div className="text-muted-foreground px-3 pt-3 pb-1 text-sm">
            <PersonBadge id={scopeNoteObject.createdByUserId} />
          </div>
          <div
            contentEditable={allowScopeNoteEditing}
            onBlur={async (e) => await updateOrCreateScopeNoteObject(e.currentTarget.innerText)}
            ref={containerRef}
            className={`overflow-y-auto px-3 pt-1 pb-3 text-sm leading-relaxed whitespace-pre-wrap ${
              fillVerticalSpace ? "max-h-full" : "max-h-[40vh]"
            }`}
          >
            {highlightedSegments.map((segment) => (
              <span
                key={`${segment.startIndex}-${segment.endIndex}`}
                className={segment.isHighlighted ? "highlight-target rounded px-1" : undefined}
                style={segment.isHighlighted ? { backgroundColor: HIGHLIGHT_COLOR } : undefined}
                data-start-index={segment.startIndex}
                data-end-index={segment.endIndex}
              >
                {segment.text}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatScopeNoteDate = (dateStr: string, showTime: boolean) =>
  showTime
    ? new Date(dateStr).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : new Date(dateStr).toLocaleDateString();

const ScopeNoteDate = ({
  estimateId,
  scopeNoteObject,
  showTime = false,
}: {
  estimateId: string;
  scopeNoteObject: AddNoteBody;
  showTime?: boolean;
}) => {
  const { currentUser } = useUser();
  const isUpdatingDateEnabled = !!currentUser?.email.match(/@rebuild\.work/);

  const { updateOrCreateScopeNoteObject: _updateOrCreateScopeNoteObject, isUpdateScopeNoteLoading } = useEstimateDetail(estimateId);

  const [dateInputValue, setDateInputValue] = useState(formatScopeNoteDate(scopeNoteObject.dateCreated, showTime));

  const updateScopeNoteDate = useCallback(async () => {
    // No change made to the displayed value — nothing to persist.
    if (dateInputValue === formatScopeNoteDate(scopeNoteObject.dateCreated, showTime)) {
      return;
    }

    const parsedDate = new Date(dateInputValue);

    if (isNaN(parsedDate.getTime())) {
      toast.error("Invalid date format. Please enter a valid date.");
      setDateInputValue(formatScopeNoteDate(scopeNoteObject.dateCreated, showTime));
      return;
    }

    await _updateOrCreateScopeNoteObject({
      ...scopeNoteObject,
      dateCreated: parsedDate.toISOString(),
    });
  }, [_updateOrCreateScopeNoteObject, scopeNoteObject, dateInputValue, showTime]);

  // Non-internal users get a read-only badge that matches the mobile note card.
  if (!isUpdatingDateEnabled) {
    return <DateBadge date={scopeNoteObject.dateCreated} showTime={showTime} />;
  }

  return (
    <div className="flex flex-row items-center justify-start text-sm" onClick={(e) => e.stopPropagation()}>
      <input
        type="text"
        disabled={isUpdateScopeNoteLoading}
        value={dateInputValue}
        onChange={(e) => setDateInputValue(e.target.value)}
        onBlur={async () => await updateScopeNoteDate()}
        className={`${showTime ? "w-44" : "w-18"} border-none bg-transparent outline-none`}
      />
    </div>
  );
};
